<template>
  <v-form validate-on="submit" @submit.prevent="action">
    <div :loading="isLoading" class="d-flex flex-column pa-6">
      <!--v-alert color="info" icon="mdi-information-variant-circle"
        >Your login credentials from the Membership Online Portal will continue
        to work in the Navigator Portal.</v-alert
    -->
      <v-card-text>
        <div class="d-flex ga-2 flex-column">
          <v-text-field
            :label="label"
            hide-details="auto"
            v-model="username"
            :rules="[rules.required]"
          ></v-text-field>
          <v-text-field
            v-if="!isCreating"
            label="Password"
            type="password"
            hide-details="auto"
            v-model="password"
            :rules="[rules.required]"
          ></v-text-field>
        </div>
        <div class="d-flex flex-column mt-6 p-0 ga-2">
          <v-btn
            variant="flat"
            color="primary"
            class="w-100"
            :disabled="isLoading"
            type="submit"
            >{{ actionLabel }}</v-btn
          >
          <v-btn
            v-if="showGuestCheckout"
            variant="tonal"
            color="primary"
            class="w-100"
            :disabled="isLoading || !isValidGuestEmail"
            type="button"
            @click="continueAsGuest"
            >Continue as guest</v-btn
          >
          <p v-if="showGuestCheckout" class="text-body-2">
            If you already have a member account, log in first to use that
            account and merge your cart. Guest checkout does not require a
            password, or choose New Account to save your cart to a new account.
          </p>
          <v-btn
            v-if="showResetPassword"
            variant="flat"
            color="primary"
            class="w-100"
            :disabled="isLoading"
            @click="sendResetPassword"
            >Send Reset Password Email</v-btn
          >
          <v-divider class="my-4"></v-divider>
          <!-- Google SSO -->
          <v-btn
            v-if="isStaff"
            variant="tonal"
            color="primary"
            class="w-100 m-0"
            :disabled="isLoading"
            @click="googleSSO"
            >Google SSO</v-btn
          >
          <div
            v-else-if="!isCreating && canCreateAccount"
            class="d-flex flex-row mr-2 ml-0 p-0 ga-2"
          >
            <v-btn
              variant="tonal"
              color="primary"
              class="w-50 m-0"
              :disabled="isLoading || !username"
              @click="sendResetPassword"
              >Forgot Password</v-btn
            >
            <v-btn
              variant="tonal"
              color="primary"
              class="w-50 m-0"
              :disabled="isLoading"
              @click="isCreating = true"
              >New Account</v-btn
            >
          </div>
          <v-btn
            v-else-if="isCreating"
            variant="tonal"
            color="primary"
            class="w-100 m-0"
            :disabled="isLoading"
            @click="isCreating = false"
            >Login to Existing Account</v-btn
          >
        </div>
      </v-card-text>
    </div>
  </v-form>
</template>

<script lang="ts" setup>
import { useEnv } from "@/composables/useEnv";
import { useValidation } from "@/composables/useValidation";
import { useAuthStore } from "@/stores/auth";
import { useNotificationStore } from "@/stores/notification";
import { errorMessage } from "@/composables/generated/contracts";
import { ref } from "vue";
import { SubmitEventPromise } from "vuetify";

const props = withDefaults(
  defineProps<{ createByDefault?: boolean; allowGuestCheckout?: boolean }>(),
  { createByDefault: false, allowGuestCheckout: false },
);

const env = useEnv();
const isStaff = computed(() => env.mode == "STAFF");

const authStore = useAuthStore();
const notificationStore = useNotificationStore();

const label = computed(() => (isStaff.value ? "Username" : "Email"));

const username = ref("");
const password = ref("");

const showResetPassword = ref(false);

const isLoading = ref(false);

const isCreating = ref(props.createByDefault);
// Every portal visitor holds a guest session; it can become an account until it has an email.
const canCreateAccount = computed(
  () => !authStore.isProtoMember || !authStore.accessToken?.claims.email,
);
const showGuestCheckout = computed(
  () =>
    props.allowGuestCheckout && authStore.isProtoMember && !isCreating.value,
);
watch(
  canCreateAccount,
  (allowed) => {
    if (!allowed) isCreating.value = false;
  },
  { immediate: true },
);

const actionLabel = computed(() =>
  isCreating.value ? "Create Account" : "Login",
);

const emit = defineEmits<{
  submit: [result: string];
  failure: [error: unknown];
  continueAsGuest: [email: string];
}>();

const validation = useValidation();
const rules = {
  required: (value: string) => !!value || "Required.",
  email: validation.email,
};
const isValidGuestEmail = computed(
  () => !!username.value.trim() && rules.email(username.value.trim()) === true,
);

function continueAsGuest() {
  if (!props.allowGuestCheckout || !authStore.isProtoMember) return;
  if (!isValidGuestEmail.value) return;
  emit("continueAsGuest", username.value.trim());
}

const sendResetPassword = async () => {
  try {
    isLoading.value = true;
    await authStore.sendUpdatePasswordEmail(username.value);
    isLoading.value = false;
    notificationStore.addSnackbar(
      "Please check your email for a link to finish the password reset",
      "info",
    );
  } catch (error) {
    notificationStore.addSnackbar(
      "Reset password failed. Please try again.",
      "error",
    );
  } finally {
    isLoading.value = false;
  }
};

watch(
  () => username.value,
  () => {
    showResetPassword.value = false;
  },
);

const action = async (a: SubmitEventPromise) => {
  const b = await a;
  if (!b.valid) {
    return Promise.resolve();
  }
  if (isCreating.value) {
    return await create();
  } else {
    return await login();
  }
};

const create = async () => {
  try {
    isLoading.value = true;
    await authStore.createAuthorizedPerson(username.value);
    emit("submit", "created");
  } catch (e: any) {
    emit("failure", e);
    if (e?.response?.data?.snackBarError == "PERSON_EXISTS") {
      notificationStore.addSnackbar(
        "That email already exists, please reset the password or login below",
        "error",
      );
      isCreating.value = false;
    } else if (e?.response?.data?.errors) {
      e.response.data.errors.forEach((error: string) => {
        notificationStore.addSnackbar(error, "error");
      });
    } else {
      notificationStore.addSnackbar(
        errorMessage(e) || "Failed to create account",
        "error",
      );
    }
  } finally {
    isLoading.value = false;
  }
};

const login = async () => {
  try {
    isLoading.value = true;
    await authStore.login(username.value, password.value);
    isLoading.value = false;
    emit("submit", "login");
  } catch (error: unknown) {
    emit("failure", error);
    const responseData =
      error && typeof error === "object" && "response" in error
        ? error.response &&
          typeof error.response === "object" &&
          "data" in error.response
          ? error.response.data
          : undefined
        : undefined;
    const errorCode =
      responseData &&
      typeof responseData === "object" &&
      "error" in responseData
        ? responseData.error
        : undefined;
    const snackbarError =
      responseData &&
      typeof responseData === "object" &&
      "snackBarError" in responseData
        ? responseData.snackBarError
        : undefined;
    if (errorCode == "PASSWORDCLEARED") {
      showResetPassword.value = true;
      notificationStore.addSnackbar(
        "This user requires a password reset. Click the button to send a reset email",
        "warning",
      );
    } else if (typeof snackbarError === "string" && snackbarError.length > 0) {
      notificationStore.addSnackbar(snackbarError, "error");
    } else {
      notificationStore.addSnackbar("Login failed. Please try again.", "error");
    }
  } finally {
    isLoading.value = false;
  }
};

const googleSSO = async () => {
  try {
    const url = authStore.googleSSOUrl;
    window.location.href = url;
  } catch (error) {
    emit("failure", error);
    notificationStore.addSnackbar(
      "Google SSO failed. Please try username/password instead.",
      "error",
    );
  } finally {
    isLoading.value = false;
  }
};
</script>

<style lang="scss" scoped></style>
